import { z as zod } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';

import { useResponsive } from 'src/hooks/use-responsive';

import { useTranslate } from 'src/locales';

import { Form, Field } from 'src/components/hook-form';
import { ReCaptchaTerms } from 'src/components/recaptcha';

// ----------------------------------------------------------------------

export const ReviewSchema = (t) =>
  zod.object({
    rating: zod.number().min(1, { message: t('Rating must be greater than or equal to 1') }),
    comment: zod.string().min(1, { message: t('Your review is required') }),
    email: zod
      .string()
      .min(1, { message: t('Invalid email') })
      .email({ message: t('Invalid email') }),
    country: zod.string(),
    photoURL: zod.string(),
    authorUID: zod.string(),
    displayName: zod.string(),
  });

export function ReviewNewForm({ id, user, collection, onClose, addNewReview, ...other }) {
  const mdUp = useResponsive('up', 'md');

  const { t, currentLang } = useTranslate();

  const defaultValues = {
    rating: 0,
    comment: '',
    photoURL: '',
    authorUID: '',
    displayName: 'Anonymous user',
    country: currentLang.countryName,
    email: `user@anonymous.${currentLang.code}`,
  };

  const methods = useForm({
    mode: 'all',
    resolver: zodResolver(ReviewSchema(t)),
    defaultValues,
    values: {
      ...defaultValues,
      authorUID: user?.id ?? '',
      photoURL: user?.photoURL ?? '',
      country: user?.country || currentLang.countryName,
      displayName: user?.displayName || 'Anonymous user',
      email: user?.email || `user@anonymous.${currentLang.code}`,
    },
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onCancel = () => {
    onClose();
    reset();
  };

  const onSubmit = handleSubmit((data) => {
    addNewReview(data);
    onCancel();
  });

  return (
    <Dialog
      fullWidth
      scroll="body"
      onClose={onClose}
      fullScreen={!mdUp}
      slotProps={{
        paper: { sx: { height: 'auto', verticalAlign: { xs: 'bottom', md: 'middle' } } },
      }}
      {...other}
    >
      <Form methods={methods} onSubmit={onSubmit}>
        <DialogContent>
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t('Rating')}:
            </Typography>
            <Field.Rating name="rating" />
          </Box>

          <Field.Text
            rows={3}
            multiline
            name="comment"
            label={t('Comment')}
            slotProps={{ textField: { inputLabel: { shrink: true } } }}
            sx={{ mt: 3 }}
          />

          <ReCaptchaTerms sx={{ mt: 2 }} />
        </DialogContent>

        <DialogActions>
          <Button color="inherit" variant="outlined" onClick={onCancel}>
            {t('Cancel')}
          </Button>
          <Button type="submit" variant="contained" loading={isSubmitting}>
            {t('Post')}
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
}
